import { GOOGLE } from "../../../../utils/constants";
import {
  getDefaultUtmParamsByPlatform,
  getFixedUtmKeysForPlatform,
} from "./utmParametersConfig";

const isUtmValueEmpty = (value) =>
  value === undefined || value === null || String(value).trim().length === 0;

const applyGoogleUtmNamePresets = ({
  fixedParams,
  platform,
  googleUtmNames,
}) => {
  if (platform !== GOOGLE || !googleUtmNames) return;

  const { campaignName, creativeName } = googleUtmNames;
  const campaignTrimmed = String(campaignName ?? "").trim();
  const creativeTrimmed = String(creativeName ?? "").trim();

  if (isUtmValueEmpty(fixedParams.utm_campaign) && campaignTrimmed) {
    fixedParams.utm_campaign = campaignTrimmed;
  }
  if (isUtmValueEmpty(fixedParams.utm_content) && creativeTrimmed) {
    fixedParams.utm_content = creativeTrimmed;
  }
};

/**
 * Parses a UTM query string into an object.
 * Input: "utm_source=meta+ads&utm_medium={{adset.name}}"
 * Output: { utm_source: "meta+ads", utm_medium: "{{adset.name}}" }
 */
export const parseUtmString = (value) => {
  if (!value || typeof value !== "string") return {};

  const params = {};
  const pairs = value.split("&");

  pairs.forEach((pair) => {
    const eqIndex = pair.indexOf("=");
    if (eqIndex === -1) return;
    const key = pair.substring(0, eqIndex).trim();
    const val = pair.substring(eqIndex + 1).trim();
    if (key) {
      params[key] = val;
    }
  });

  return params;
};

/**
 * Builds a UTM query string from an object.
 * Only includes params whose value is non-empty (empty string and whitespace-only are excluded).
 * Values are trimmed and internal spaces are replaced by "_".
 * Input: { utm_source: "meta ads", utm_medium: "{{adset.name}}", utm_id: "" }
 * Output: "utm_source=meta_ads&utm_medium={{adset.name}}"
 */
export const buildUtmString = (params) => {
  if (!params || typeof params !== "object") return "";

  return Object.entries(params)
    .filter(([key, value]) => {
      if (!key) return false;
      if (value === undefined || value === null) return false;
      const str = String(value).trim();
      return str.length > 0;
    })
    .map(([key, value]) => {
      const normalized = String(value).trim().replace(/\s+/g, "_");
      return `${key}=${normalized}`;
    })
    .join("&");
};

/**
 * Returns initial UTM params for the modal.
 * - If currentValue is empty: use platform defaults for all 5 fixed keys.
 * - If currentValue has any content: use only parsed values; missing fixed keys stay empty.
 * - For GOOGLE, optional googleUtmNames fills empty utm_campaign / utm_content from the form.
 */
export const getInitialUtmParams = ({
  currentValue,
  platform,
  googleUtmNames,
}) => {
  const trimmed = typeof currentValue === "string" ? currentValue.trim() : "";
  const hasIncomingValue = trimmed.length > 0;
  const defaults = hasIncomingValue
    ? null
    : getDefaultUtmParamsByPlatform(platform);
  const current = parseUtmString(currentValue);
  const fixedKeysForPlatform = getFixedUtmKeysForPlatform(platform);

  const fixedParams = {};
  fixedKeysForPlatform.forEach((key) => {
    if (current[key] !== undefined) {
      fixedParams[key] = current[key];
    } else if (defaults) {
      fixedParams[key] = defaults[key] ?? "";
    } else {
      fixedParams[key] = "";
    }
  });

  applyGoogleUtmNamePresets({ fixedParams, platform, googleUtmNames });

  const customParams = Object.entries(current)
    .filter(([key]) => !fixedKeysForPlatform.includes(key))
    .map(([key, value]) => ({ key, value }));

  return { fixedParams, customParams };
};
